export interface RetryOptions {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  shouldRetry: (error: Error, attempt: number) => boolean;
}

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 8000,
  backoffMultiplier: 2,
  shouldRetry: () => true, // By default, retry all errors
} as const;

// Retry function with exponential backoff
export const retryWithBackoff = async <T>(fn: () => Promise<T>, options: Partial<RetryOptions> = {}): Promise<T> => {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let delay = config.initialDelay;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === config.maxRetries) {
        throw error; // Final attempt failed
      }

      // Check if we should retry based on the error
      if (
        config.shouldRetry &&
        !config.shouldRetry(error instanceof Error ? error : new Error('Unknown error'), attempt)
      ) {
        throw error;
      }

      // Wait before retrying
      const currentDelay = delay;
      await new Promise<void>((resolve) => setTimeout(resolve, currentDelay));
      delay = Math.min(delay * config.backoffMultiplier, config.maxDelay);
    }
  }
  throw new Error('Retry logic error'); // Should never reach here
};
